import React, { useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Select from "react-select";

export default function FormControl({
  name,
  divClass = "space-y-1",
  className = "",
  labelClass = "",
  id = "",
  placeholder = "",
  type = "text",
  options = [],
  displayValue = "name",
  error,
  label,
  required = false,
  onChange = () => {},
  onBlur = () => {},
  maxlength = "",
  minlength = "",
  min = "",
  value = "",
  disabled = false,
  autoComplete = "off",
  showEye = false,
  onKeyDown,
  style = {},
  rows = 4,
}) {
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const val = e && e.target !== undefined ? e.target.value : e;
    if (onChange) {
      onChange(val);
    }
  };

  return (
    <div className={`w-full ${divClass || "space-y-1"}`}>
      {label && (
        <label
          htmlFor={id || name}
          className={`block text-xs font-bold uppercase tracking-wider text-slate-900 mb-1 ${labelClass}`}
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {type === "phone" ? (
        <div className="relative w-full">
          <PhoneInput
            country="us"
            enableSearch={true}
            autoFormat={true}
            specialLabel=""
            value={value || ""}
            placeholder={placeholder || "+1 (800) 555-0199"}
            disabled={disabled}
            onChange={(phone, country, e, formattedValue) => {
              const dialCode = country?.dialCode || "1";
              const cleanPhone = (phone || "").replace(/\D/g, "");
              if (!cleanPhone || cleanPhone === dialCode) {
                onChange("");
              } else {
                onChange(
                  formattedValue ||
                    (phone ? (phone.startsWith("+") ? phone : `+${phone}`) : "")
                );
              }
            }}
            buttonClass={`!border-[1.5px] !border-r-0 !bg-slate-50 !rounded-l-xl ${
              error ? "!border-red-500" : "!border-slate-300"
            }`}
            inputClass={`!w-full !bg-white !text-sm !text-slate-900 placeholder:!text-slate-400 !rounded-xl !h-10 sm:!h-11 !flex !items-center !px-4 !pl-12 !outline-none !shadow-none focus:!outline-none focus:!ring-0 focus:!shadow-none !border-[1.5px] ${
              error
                ? "!border-red-500 focus:!border-red-500"
                : "!border-slate-300 focus:!border-slate-300"
            } ${className}`}
            dropdownClass="!rounded-xl !shadow-xl !border !border-slate-200 !text-slate-900 !z-50"
          />
        </div>
      ) : type === "select" ? (
        <div className="relative w-full">
          <Select
            id={id || name}
            isDisabled={disabled}
            placeholder={placeholder || "Select option..."}
            options={options.map((opt) => ({
              value: opt.id !== undefined ? opt.id : opt.value,
              label: opt[displayValue] || opt.name || opt.label,
            }))}
            value={
              (() => {
                const found = options.find(
                  (opt) => (opt.id !== undefined ? opt.id : opt.value) === value
                );
                return found
                  ? {
                      value: found.id !== undefined ? found.id : found.value,
                      label: found[displayValue] || found.name || found.label,
                    }
                  : null;
              })()
            }
            onChange={(selected) => {
              if (onChange) {
                onChange(selected ? selected.value : "");
              }
            }}
            styles={{
              control: (base, state) => ({
                ...base,
                borderRadius: "0.75rem",
                borderColor: error
                  ? "#ef4444"
                  : state.isFocused
                  ? "#115B9D"
                  : "#cbd5e1",
                borderWidth: "1.5px",
                minHeight: "42px",
                backgroundColor: disabled ? "#f8fafc" : "#ffffff",
                boxShadow: "none",
                "&:hover": {
                  borderColor: state.isFocused ? "#115B9D" : "#94a3b8",
                },
              }),
              option: (base, state) => ({
                ...base,
                fontSize: "13px",
                fontWeight: state.isSelected ? "700" : "500",
                backgroundColor: state.isSelected
                  ? "#115B9D"
                  : state.isFocused
                  ? "#f0f9ff"
                  : "#ffffff",
                color: state.isSelected ? "#ffffff" : "#1e293b",
                cursor: "pointer",
                padding: "8px 12px",
              }),
              menu: (base) => ({
                ...base,
                borderRadius: "0.75rem",
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                border: "1px solid #e2e8f0",
                zIndex: 9999,
                overflow: "hidden",
              }),
              singleValue: (base) => ({
                ...base,
                fontSize: "14px",
                fontWeight: "600",
                color: "#0f172a",
              }),
              placeholder: (base) => ({
                ...base,
                fontSize: "13px",
                color: "#94a3b8",
              }),
            }}
            className={className}
          />
        </div>
      ) : type === "textarea" ? (
        <div className="relative w-full">
          <textarea
            name={name}
            id={id || name}
            rows={rows}
            disabled={disabled}
            required={required}
            placeholder={placeholder}
            value={value ?? ""}
            maxLength={maxlength}
            minLength={minlength}
            onChange={handleChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            style={{
              outline: "none",
              boxShadow: "none",
              ...style,
            }}
            className={`w-full bg-white text-sm text-slate-900 placeholder:text-slate-400 rounded-xl p-3.5 !outline-none !shadow-none focus:!outline-none focus:!ring-0 focus:!shadow-none border-[1.5px] ${
              error
                ? "!border-red-500 focus:!border-red-500"
                : "!border-slate-300 focus:!border-slate-300"
            } ${
              disabled ? "bg-slate-100 cursor-not-allowed text-slate-500" : ""
            } ${className}`}
          />
        </div>
      ) : (
        <div className="relative w-full">
          <input
            type={showEye ? (showPassword ? "text" : "password") : type}
            name={name}
            id={id || name}
            autoComplete={autoComplete}
            disabled={disabled}
            required={required}
            placeholder={placeholder}
            value={value ?? ""}
            maxLength={maxlength}
            minLength={minlength}
            min={min}
            onChange={handleChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            style={{
              outline: "none",
              boxShadow: "none",
              ...style,
            }}
            className={`w-full bg-white text-sm text-slate-900 placeholder:text-slate-400 rounded-xl h-10 sm:h-11 flex items-center px-4 !outline-none !shadow-none focus:!outline-none focus:!ring-0 focus:!shadow-none border-[1.5px] ${
              error
                ? "!border-red-500 focus:!border-red-500"
                : "!border-slate-300 focus:!border-slate-300"
            } ${showEye ? "pr-11" : ""} ${
              disabled ? "bg-slate-100 cursor-not-allowed text-slate-500" : ""
            } ${className}`}
          />

          {showEye && (
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900 p-1 focus:outline-none cursor-pointer z-10 transition-colors"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <FaEye className="w-4 h-4 text-slate-600" />
              ) : (
                <FaEyeSlash className="w-4 h-4 text-slate-600" />
              )}
            </button>
          )}
        </div>
      )}

      {error ? (
        <div className="text-red-500 text-xs mt-1 font-medium flex items-center gap-1">
          {error}
        </div>
      ) : null}
    </div>
  );
}
